/**
 * DropsListingPage
 *
 * Public page listing active NFT drops available for minting.
 * Route: /drops
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { DefaultPageLayout } from '../ui/layouts/DefaultPageLayout';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Button } from '../ui/components/Button';
import { FeatherChevronDown, FeatherStar, FeatherCheck, FeatherX } from '@subframe/core';
import { useAuthStore } from '../store/authStore';
import {
  getDrops,
  type Drop,
  formatDropsToXrp,
  getMintProgress,
  getTimeUntilStart,
  formatMintPhase,
} from '../api/drops';

type StatusFilter = 'active' | 'upcoming' | 'completed' | undefined;
type PhaseFilter = 'allowlist' | 'allowlist_2' | 'public' | undefined;

interface FilterOption {
  id: string;
  label: string;
  status?: StatusFilter;
  phase?: PhaseFilter;
  onMyAllowlist?: boolean;
  requiresAuth?: boolean;
  isHeader?: boolean;
}

const filterOptions: FilterOption[] = [
  // Minting Now section
  { id: 'header-minting', label: 'Minting Now', isHeader: true },
  { id: 'active', label: 'All', status: 'active' },
  { id: 'active-allowlist', label: 'Allowlist Phase 1', status: 'active', phase: 'allowlist' },
  { id: 'active-allowlist-2', label: 'Allowlist Phase 2', status: 'active', phase: 'allowlist_2' },
  { id: 'active-public', label: 'Open to Public', status: 'active', phase: 'public' },
  // My Allowlists (auth required)
  { id: 'my-allowlist', label: "I'm on Allowlist", onMyAllowlist: true, requiresAuth: true },
  // Browse section
  { id: 'header-browse', label: 'Browse', isHeader: true },
  { id: 'all', label: 'All Drops' },
  { id: 'upcoming', label: 'Upcoming', status: 'upcoming' },
  { id: 'completed', label: 'Completed', status: 'completed' },
];

interface DropCardProps {
  drop: Drop;
}

/**
 * Check if a drop should be shown on the listing page.
 * Ready drops without any start date should not be shown.
 */
function shouldShowDrop(drop: Drop): boolean {
  // Active, paused, and completed drops are always shown
  if (drop.status !== 'ready') return true;
  // Ready drops need at least one start date to be shown
  return !!(drop.allowlist_start_at || drop.allowlist_2_start_at || drop.public_start_at);
}

/**
 * Format transfer fee (stored as basis points, e.g., 8880 = 8.88%) to display string
 */
function formatRoyalty(transferFee: number | undefined | null): string {
  if (transferFee === undefined || transferFee === null) return '0';
  const percentage = transferFee / 1000;
  // Show up to 2 decimal places, but trim trailing zeros
  if (percentage === Math.floor(percentage)) return percentage.toString();
  const oneDecimal = Math.round(percentage * 10) / 10;
  if (percentage === oneDecimal) return percentage.toFixed(1);
  return percentage.toFixed(2);
}

const DropCard: React.FC<DropCardProps> = ({ drop }) => {
  const progress = getMintProgress(drop.minted_count, drop.total_supply);
  const timeInfo = getTimeUntilStart(
    drop.allowlist_start_at,
    drop.public_start_at,
    drop.allowlist_enabled,
    drop.allowlist_2_start_at,
    drop.allowlist_2_enabled
  );

  // Determine current price to display based on current phase
  const currentPrice =
    drop.mint_phase === 'allowlist' && drop.allowlist_mint_price_drops !== null
      ? drop.allowlist_mint_price_drops
      : drop.mint_phase === 'allowlist_2' && drop.allowlist_2_mint_price_drops !== null
        ? drop.allowlist_2_mint_price_drops
        : drop.mint_price_drops;

  // Get thumbnail - could be from S3 or IPFS
  const thumbnailUrl = drop.thumbnail_url || null;

  // Get the user's allowlist status for both phases
  const isOnAllowlist = drop.userAllowlistStatus?.isOnAllowlist;
  const isOnAllowlist2 = drop.userAllowlistStatus?.isOnAllowlist2;
  // userAllowlistStatus is null if user not logged in, or if drop doesn't have allowlist
  const hasAllowlistInfo = drop.userAllowlistStatus !== null && drop.userAllowlistStatus !== undefined;

  // Determine which allowlist status to show based on current phase
  const currentPhaseAllowlistStatus = drop.mint_phase === 'allowlist_2' ? isOnAllowlist2 : isOnAllowlist;

  // Link to collection page if available, otherwise fall back to drop redirect
  const linkTo = drop.collection_slug
    ? `/collections/${drop.collection_slug}`
    : `/drops/${drop.slug}`;

  return (
    <Link
      to={linkTo}
      className="group block rounded-lg border border-neutral-border bg-default-background overflow-hidden hover:border-brand-500 transition-colors"
    >
      {/* Image - no badges, clean look */}
      <div className="aspect-square relative overflow-hidden bg-neutral-100 dark:bg-neutral-800">
        {thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt={drop.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-subtext-color">
            <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Status badge */}
        <div className="mb-1 flex items-center gap-1.5">
          {drop.status === 'active' && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-caption font-medium bg-success-500 text-white">
              Live
            </span>
          )}
          {drop.status === 'completed' && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-caption font-medium bg-error-500 text-white">
              Ended
            </span>
          )}
          {drop.status === 'paused' && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-caption font-medium bg-warning-500 text-white">
              Paused
            </span>
          )}
          {drop.status === 'ready' && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-caption font-medium bg-neutral-500 text-white">
              Upcoming
            </span>
          )}
          {drop.has_mint_rewards && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-caption font-medium bg-brand-500 text-white">
              Contest
            </span>
          )}
        </div>

        {/* Name */}
        <h3 className="text-body-bold font-body-bold text-default-font truncate">
          {drop.name}
        </h3>

        {/* Price */}
        <div className="mt-2 flex items-center justify-between">
          <span className="text-caption text-subtext-color">Price</span>
          <span className="text-body-bold font-body-bold text-default-font">
            {formatDropsToXrp(currentPrice)}
          </span>
        </div>

        {/* Royalty */}
        <div className="mt-1 flex items-center justify-between">
          <span className="text-caption text-subtext-color">Royalty</span>
          <span className="text-caption text-default-font">
            {formatRoyalty(drop.transfer_fee)}%
          </span>
        </div>

        {/* Progress */}
        <div className="mt-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-caption text-subtext-color">Minted</span>
            <span className="text-caption text-default-font">
              {drop.minted_count.toLocaleString()} / {drop.total_supply?.toLocaleString() || '?'}
            </span>
          </div>
          <div className="h-1.5 bg-neutral-200 dark:bg-neutral-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-brand-500 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Status Section - fixed height for consistent cards */}
        <div className="mt-3 pt-3 border-t border-neutral-border min-h-[88px]">
          {drop.status === 'paused' ? (
            <p className="text-caption text-warning-500">Status: Paused</p>
          ) : (drop.status === 'active' || drop.status === 'ready' || drop.status === 'completed') ? (
            <PhaseScheduleDisplay
              drop={drop}
              hasAllowlistInfo={hasAllowlistInfo}
              isOnAllowlist={isOnAllowlist}
              isOnAllowlist2={isOnAllowlist2}
            />
          ) : null}
        </div>
      </div>
    </Link>
  );
};

interface PhaseScheduleDisplayProps {
  drop: Drop;
  hasAllowlistInfo: boolean;
  isOnAllowlist: boolean | undefined;
  isOnAllowlist2: boolean | undefined;
}

/**
 * Displays the full phase schedule for a drop in a table format
 * showing phase, eligibility, and timing
 */
const PhaseScheduleDisplay: React.FC<PhaseScheduleDisplayProps> = ({
  drop,
  hasAllowlistInfo,
  isOnAllowlist,
  isOnAllowlist2,
}) => {
  const now = new Date();
  const isActive = drop.status === 'active';
  const isUpcoming = drop.status === 'ready';
  const isCompleted = drop.status === 'completed';

  // Determine phase states
  const phase1Started = drop.allowlist_start_at && new Date(drop.allowlist_start_at) <= now;
  const phase2Started = drop.allowlist_2_start_at && new Date(drop.allowlist_2_start_at) <= now;
  const publicStarted = drop.public_start_at && new Date(drop.public_start_at) <= now;

  // Build list of phases to display
  const phases: Array<{
    id: string;
    label: string;
    isCurrent: boolean;
    isEligible: boolean | undefined; // undefined = don't show eligibility (not logged in)
    startAt: string | null;
    isStarted: boolean;
    priceDrops: number | null;
  }> = [];

  if (drop.allowlist_enabled) {
    phases.push({
      id: 'allowlist',
      label: drop.allowlist_2_enabled ? 'Phase 1' : 'Allowlist',
      isCurrent: isActive && drop.mint_phase === 'allowlist',
      isEligible: hasAllowlistInfo ? isOnAllowlist : undefined,
      startAt: drop.allowlist_start_at,
      isStarted: isCompleted || !!phase1Started,
      priceDrops: drop.allowlist_mint_price_drops,
    });
  }

  if (drop.allowlist_2_enabled) {
    phases.push({
      id: 'allowlist_2',
      label: 'Phase 2',
      isCurrent: isActive && drop.mint_phase === 'allowlist_2',
      isEligible: hasAllowlistInfo ? isOnAllowlist2 : undefined,
      startAt: drop.allowlist_2_start_at,
      isStarted: isCompleted || !!phase2Started,
      priceDrops: drop.allowlist_2_mint_price_drops,
    });
  }

  phases.push({
    id: 'public',
    label: 'Public',
    isCurrent: isActive && drop.mint_phase === 'public',
    isEligible: hasAllowlistInfo ? true : undefined, // Everyone eligible for public
    startAt: drop.public_start_at,
    isStarted: isCompleted || !!publicStarted,
    priceDrops: drop.mint_price_drops,
  });

  // Render status cell
  const renderStatus = (phase: typeof phases[0]) => {
    if (phase.isCurrent) {
      return <span className="text-green-400 font-medium">Live</span>;
    }
    if (phase.isStarted && !phase.isCurrent) {
      // Past phase
      return <span className="text-red-400">Ended</span>;
    }
    if (!phase.startAt) {
      return <span className="text-subtext-color">TBD</span>;
    }
    // Future phase - show countdown with "in" prefix
    return <CountdownTimerInline targetDate={new Date(phase.startAt)} prefix="in " />;
  };

  // Render eligibility cell
  const renderEligibility = (phase: typeof phases[0]) => {
    if (phase.isEligible === undefined) {
      // Not logged in - show dash
      return <span className="text-subtext-color">—</span>;
    }
    if (phase.isEligible) {
      return <FeatherCheck className="w-4 h-4 text-green-400" />;
    }
    return <FeatherX className="w-4 h-4 text-red-400" />;
  };

  return (
    <div className="text-caption grid grid-cols-[0.75rem_1fr_auto_auto_auto] gap-x-1.5 items-center">
      {/* Header row */}
      <div className="pb-1 text-subtext-color" />
      <div className="pb-1 text-subtext-color">Phase</div>
      <div className="pb-1 text-subtext-color text-right">Price</div>
      <div className="pb-1 text-subtext-color text-center">Eligible?</div>
      <div className="pb-1 text-subtext-color text-right">Status</div>

      {/* Separator */}
      <div className="col-span-5 border-b border-neutral-border mb-0.5" />

      {/* Data rows */}
      {phases.map((phase) => (
        <React.Fragment key={phase.id}>
          <div className="py-0.5 flex justify-center">
            {phase.isCurrent && (
              <div className="w-1.5 h-1.5 rounded-full bg-success-500" />
            )}
          </div>
          <div className={`py-0.5 whitespace-nowrap ${
            phase.isCurrent ? 'font-medium text-default-font' : 'text-subtext-color'
          }`}>
            {phase.label}
          </div>
          <div className={`py-0.5 text-right whitespace-nowrap ${
            phase.isCurrent ? 'font-medium text-default-font' : 'text-subtext-color'
          }`}>
            {formatDropsToXrp(phase.priceDrops)}
          </div>
          <div className={`py-0.5 flex justify-center ${
            phase.isCurrent ? 'text-default-font' : 'text-subtext-color'
          }`}>
            {renderEligibility(phase)}
          </div>
          <div className={`py-0.5 text-right whitespace-nowrap ${
            phase.isCurrent ? 'text-default-font' : 'text-subtext-color'
          }`}>
            {renderStatus(phase)}
          </div>
        </React.Fragment>
      ))}
    </div>
  );
};

interface CountdownTimerInlineProps {
  targetDate: Date;
  prefix?: string;
}

/**
 * Inline countdown timer (no label, just the time)
 */
const CountdownTimerInline: React.FC<CountdownTimerInlineProps> = ({ targetDate, prefix = '' }) => {
  const [timeLeft, setTimeLeft] = useState(getTimeLeftCalc(targetDate));

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(getTimeLeftCalc(targetDate));
    }, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  function getTimeLeftCalc(target: Date) {
    const now = new Date();
    const diff = target.getTime() - now.getTime();

    if (diff <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    return { days, hours, minutes, seconds };
  }

  // Build countdown string - compact format
  const parts: string[] = [];
  if (timeLeft.days > 0) parts.push(`${timeLeft.days}d`);
  if (timeLeft.days > 0 || timeLeft.hours > 0) parts.push(`${timeLeft.hours}h`);
  // Only show minutes if less than 1 day remaining
  if (timeLeft.days === 0) {
    parts.push(`${timeLeft.minutes}m`);
  }
  // Only show seconds if less than 1 hour remaining
  if (timeLeft.days === 0 && timeLeft.hours === 0) {
    parts.push(`${timeLeft.seconds}s`);
  }

  return <span className="text-default-font">{prefix}{parts.join(' ')}</span>;
};

interface CountdownTimerProps {
  targetDate: Date;
  label: string;
}

const CountdownTimer: React.FC<CountdownTimerProps> = ({ targetDate, label }) => {
  const [timeLeft, setTimeLeft] = useState(getTimeLeft(targetDate));

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(getTimeLeft(targetDate));
    }, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  function getTimeLeft(target: Date) {
    const now = new Date();
    const diff = target.getTime() - now.getTime();

    if (diff <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    return { days, hours, minutes, seconds };
  }

  // Build countdown string, only showing relevant units
  const parts: string[] = [];
  if (timeLeft.days > 0) parts.push(`${timeLeft.days}d`);
  if (timeLeft.days > 0 || timeLeft.hours > 0) parts.push(`${timeLeft.hours}h`);
  // Only show minutes and seconds if less than 1 day remaining
  if (timeLeft.days === 0) {
    parts.push(`${timeLeft.minutes}m`);
    parts.push(`${timeLeft.seconds}s`);
  }

  return (
    <p className="text-caption text-default-font">
      {label}: {parts.join(' ')}
    </p>
  );
};

export const DropsListingPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated } = useAuthStore();

  // Initialize filter from URL
  const initFilter = searchParams.get('filter') || 'all';
  const [drops, setDrops] = useState<Drop[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState(initFilter);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;

  // Get selectable filters (exclude headers)
  const selectableFilters = filterOptions.filter(f => !f.isHeader);

  // Get current filter config (default to 'all' if not found)
  const defaultFilter = selectableFilters.find(f => f.id === 'all') || selectableFilters[0];
  const currentFilterConfig = selectableFilters.find(f => f.id === activeFilter) || defaultFilter;

  // Get available filters for dropdown (hide auth-required filters when not logged in)
  const availableFilters = filterOptions.filter(
    f => !f.requiresAuth || isAuthenticated
  );

  const fetchDrops = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await getDrops({
        page,
        limit,
        status: currentFilterConfig.status,
        phase: currentFilterConfig.phase,
        onMyAllowlist: currentFilterConfig.onMyAllowlist,
      });

      setDrops(result.drops);
      setTotal(result.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load drops');
    } finally {
      setLoading(false);
    }
  }, [page, currentFilterConfig, limit]);

  useEffect(() => {
    fetchDrops();
  }, [fetchDrops]);

  // Update URL when filter changes
  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    if (activeFilter && activeFilter !== 'all') {
      newParams.set('filter', activeFilter);
    } else {
      newParams.delete('filter');
    }
    setSearchParams(newParams, { replace: true });
  }, [activeFilter, setSearchParams, searchParams]);

  const handleFilterChange = (filterId: string) => {
    setActiveFilter(filterId);
    setPage(1);
    setShowFilterDropdown(false);
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <DefaultPageLayout>
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-heading-1 font-heading-1 text-default-font">
              NFT Drops
            </h1>
            <p className="text-body text-subtext-color mt-1">
              Mint exclusive NFT collections from verified creators
            </p>
          </div>

          {/* Filter Dropdown */}
          <div className="relative">
            <Button
              variant="neutral-secondary"
              onClick={() => setShowFilterDropdown(!showFilterDropdown)}
              iconRight={<FeatherChevronDown />}
            >
              {currentFilterConfig.label}
            </Button>

            {showFilterDropdown && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowFilterDropdown(false)}
                />
                <div className="absolute right-0 mt-2 w-56 rounded-lg border border-neutral-border bg-default-background shadow-lg z-50 py-1">
                  {availableFilters.map((option, index) => {
                    // Add separator before "My Allowlists" and "Browse" section
                    const showSeparator = option.id === 'my-allowlist' || option.id === 'header-browse';

                    // Render section headers
                    if (option.isHeader) {
                      return (
                        <React.Fragment key={option.id}>
                          {index > 0 && (
                            <div className="my-1 border-t border-neutral-border" />
                          )}
                          <div className="px-4 py-1.5 text-caption font-caption text-subtext-color uppercase tracking-wide">
                            {option.label}
                          </div>
                        </React.Fragment>
                      );
                    }

                    return (
                      <React.Fragment key={option.id}>
                        {showSeparator && index > 0 && (
                          <div className="my-1 border-t border-neutral-border" />
                        )}
                        <button
                          onClick={() => handleFilterChange(option.id)}
                          className={`w-full px-4 py-2 text-left text-body font-body hover:bg-brand-600 hover:text-white flex items-center gap-2 ${
                            activeFilter === option.id
                              ? 'text-brand-600 bg-neutral-100 dark:bg-neutral-800'
                              : 'text-default-font'
                          }`}
                        >
                          {option.id === 'my-allowlist' && (
                            <FeatherStar className="w-4 h-4 text-warning-500" />
                          )}
                          {option.label}
                        </button>
                      </React.Fragment>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <LoadingSpinner size="lg" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20">
            <p className="text-body text-error-600 mb-4">{error}</p>
            <Button onClick={fetchDrops}>Try Again</Button>
          </div>
        ) : drops.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-16 h-16 mb-4 rounded-full bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center">
              <svg className="w-8 h-8 text-subtext-color" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <h3 className="text-heading-3 font-heading-3 text-default-font mb-2">
              No Drops Found
            </h3>
            <p className="text-body text-subtext-color text-center max-w-md">
              {currentFilterConfig.status
                ? `No ${currentFilterConfig.status} drops at the moment. Check back soon!`
                : 'No drops available yet. Check back soon for new collections!'}
            </p>
          </div>
        ) : (
          <>
            {/* Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6">
              {drops.filter(shouldShowDrop).map((drop) => (
                <DropCard key={drop.id} drop={drop} />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-8">
                <Button
                  variant="neutral-secondary"
                  size="small"
                  disabled={page === 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  Previous
                </Button>
                <span className="text-body text-subtext-color px-4">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="neutral-secondary"
                  size="small"
                  disabled={page === totalPages}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </DefaultPageLayout>
  );
};

export default DropsListingPage;
